import type {PropsWithChildren, ReactNode} from 'react';
import type {FieldValues, UseControllerProps} from 'react-hook-form';
import {useController} from 'react-hook-form';

import {typedMemo} from '#utils/react.js';

import {Anchor} from '../multi_combo_box/anchor.js';
import type {AnchorProps} from '../multi_combo_box/anchor.js';
import {MultiComboBox} from '../multi_combo_box/index.js';
import type {
  MultiComboBoxItemsProps,
  MultiComboBoxLoadItemsProps,
} from '../multi_combo_box/index.js';
import {Items} from '../multi_combo_box/items.js';
import {Search} from '../multi_combo_box/search.js';
import {SelectedItems} from '../multi_combo_box/selected_items.js';
import {Surface} from '../multi_combo_box/surface.js';
import {ToggleButton} from '../multi_combo_box/toggle_button.js';

type ComboBoxProps<T> = PropsWithChildren<
  | Omit<MultiComboBoxItemsProps<T>, 'as'>
  | Omit<MultiComboBoxLoadItemsProps<T>, 'as'>
>;

type ComboBoxFormFieldProps<TItem, TFieldValues extends FieldValues> = Omit<
  MultiComboBoxItemsProps<TItem>,
  'as' | 'asChild' | 'initialSelectedItems' | 'onChange'
> &
  UseControllerProps<TFieldValues> & {
    anchorProps?: AnchorProps<TItem>;
    children?: ReactNode;
    itemToValue: (item: TItem) => string;
    onValueChange?: (value: string) => void;
    searchPlaceholder?: string;
  };

const Root = <T,>(props: ComboBoxProps<T>) => {
  if (props.loadItems) {
    const {items: _items, isLoading: _isLoading, ...rest} = props;
    return (
      <MultiComboBox {...rest} loadItems={props.loadItems} as="ComboBox" />
    );
  }

  const {loadItems: _loadItems, ...rest} = props;
  return <MultiComboBox {...rest} as="ComboBox" />;
};
Root.displayName = 'Root';

const FormField = typedMemo(function FormField<
  TItem,
  TFieldValues extends FieldValues,
>({
  children,
  anchorProps,
  control,
  itemToValue,
  name,
  onValueChange,
  rules,
  searchPlaceholder,
  ...props
}: ComboBoxFormFieldProps<TItem, TFieldValues>) {
  const {field} = useController({control, name, rules});
  const value = typeof field.value === 'string' ? field.value : '';
  const selectedItems = props.items?.filter(
    item => itemToValue(item) === value
  );

  return (
    <Root
      {...props}
      key={`${name}-${value}-${props.items?.length ?? 0}`}
      initialSelectedItems={selectedItems}
      onChange={items => {
        const nextValue = items[0] ? itemToValue(items[0]) : '';
        field.onChange(nextValue);
        onValueChange?.(nextValue);
      }}
      asChild
    >
      {children ?? (
        <>
          <Anchor {...anchorProps} name={field.name} />
          <Surface asChild>
            <Search placeholder={searchPlaceholder} />
            <Items />
          </Surface>
        </>
      )}
    </Root>
  );
});

const ComboBox = Object.assign(Root, {
  Anchor,
  FormField,
  SelectedItems,
  ToggleButton,
  Surface,
  Search,
  Items,
});

export type {ComboBoxFormFieldProps, ComboBoxProps};
export {ComboBox};
